import React, { Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Box, Container, CssBaseline, CircularProgress } from "@mui/material";
import "./i18n"; // Initialize i18n
import HomePage from "./components/Public/HomePage.js";
const CarDetails = React.lazy(
  () => import("./components/Public/CarDetails.js"),
);
const About = React.lazy(() => import("./components/Public/About.js"));
const Contact = React.lazy(() => import("./components/Public/Contact.js"));
const AdminLogin = React.lazy(() => import("./components/Admin/AdminLogin.js"));
const AdminDashboard = React.lazy(
  () => import("./components/Admin/AdminDashboard.js"),
);
const AdminCarDetails = React.lazy(
  () => import("./components/Admin/AdminCarDetails.js"),
);
const CreateCar = React.lazy(() => import("./components/Admin/CreateCar.js"));
const EditCar = React.lazy(() => import("./components/Admin/EditCar.js"));
const PrivateRoute = React.lazy(
  () => import("./components/Admin/PrivateRoute.js"),
);
const AdminOrdersPage = React.lazy(
  () => import("./components/Admin/AdminOrdersPage.js"),
);
const AdminAffiliationsPage = React.lazy(
  () => import("./components/Admin/AdminAffiliationsPage.js"),
);
const LoginPage = React.lazy(() => import("./components/Auth/LoginPage.js"));
const RegisterPage = React.lazy(
  () => import("./components/Auth/RegisterPage.js"),
);
const VerifyEmailPage = React.lazy(
  () => import("./components/Auth/VerifyEmailPage.js"),
);
const ForgotPasswordPage = React.lazy(
  () => import("./components/Auth/ForgotPasswordPage.js"),
);
const ResetPasswordPage = React.lazy(
  () => import("./components/Auth/ResetPasswordPage.js"),
);
const FavoritesPage = React.lazy(
  () => import("./components/User/FavoritesPage.js"),
);
const ComparePage = React.lazy(
  () => import("./components/User/ComparePage.js"),
);
const ProfilePage = React.lazy(
  () => import("./components/User/ProfilePage.js"),
);
const TrackingPage = React.lazy(
  () => import("./components/User/TrackingPage.js"),
);
const AffiliatePage = React.lazy(
  () => import("./components/User/AffiliatePage.js"),
);
const CartPage = React.lazy(() => import("./components/User/CartPage.js"));
const PurchasesPage = React.lazy(
  () => import("./components/User/PurchasesPage.js"),
);

const ProtectedRoute = React.lazy(
  () => import("./components/Common/ProtectedRoute.js"),
);
import { AuthProvider } from "./context/AuthContext.js";
import { ThemeProvider } from "./context/ThemeContext.js";
import { CompareProvider } from "./context/CompareContext.js";
import { CartProvider } from "./context/CartContext.js";
import { FilterProvider } from "./context/FilterContext.js";
import Navbar from "./components/Layout/Navbar";
import Footer from "./components/Layout/Footer";
// import ChatBot from './components/Common/ChatBot';

function App() {
  return (
    <Suspense
      fallback={
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
        >
          <CircularProgress />
        </Box>
      }
    >
      <ThemeProvider>
        <CssBaseline />
        <AuthProvider>
          <CartProvider>
            <CompareProvider>
              <FilterProvider>
                {/* <ChatBot /> */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    minHeight: "100vh",
                    background: (theme) => theme.palette.background.gradient,
                  }}
                >
                  <Navbar />
                  <Box
                    component="main"
                    sx={{
                      flexGrow: 1,
                    }}
                  >
                    <Routes>
                      {/* Public routes - HomePage sans Container pour pleine largeur */}
                      <Route path="/" element={<HomePage />} />

                      {/* Autres routes publiques avec Container */}
                      <Route path="/compare" element={<ComparePage />} />
                      <Route path="/cars/:id" element={<CarDetails />} />
                      <Route
                        path="/about"
                        element={
                          <Container maxWidth="lg" sx={{ py: 4 }}>
                            <About />
                          </Container>
                        }
                      />
                      <Route
                        path="/contact"
                        element={
                          <Container maxWidth="lg" sx={{ py: 4 }}>
                            <Contact />
                          </Container>
                        }
                      />
                      <Route
                        path="/login"
                        element={
                          <Container maxWidth="lg" sx={{ py: 4 }}>
                            <LoginPage />
                          </Container>
                        }
                      />
                      <Route
                        path="/register"
                        element={
                          <Container maxWidth="lg" sx={{ py: 4 }}>
                            <RegisterPage />
                          </Container>
                        }
                      />
                      <Route
                        path="/verify-email"
                        element={<VerifyEmailPage />}
                      />
                      <Route
                        path="/forgot-password"
                        element={<ForgotPasswordPage />}
                      />
                      <Route
                        path="/reset-password"
                        element={<ResetPasswordPage />}
                      />
                      <Route
                        path="/favorites"
                        element={
                          <ProtectedRoute>
                            <Container maxWidth="lg" sx={{ py: 4 }}>
                              <FavoritesPage />
                            </Container>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/profile"
                        element={
                          <ProtectedRoute>
                            <Container maxWidth="lg" sx={{ py: 4 }}>
                              <ProfilePage />
                            </Container>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/cart"
                        element={
                          <ProtectedRoute>
                            <CartPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/tracking"
                        element={
                          <ProtectedRoute>
                            <Container maxWidth="lg" sx={{ py: 4 }}>
                              <TrackingPage />
                            </Container>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/purchases"
                        element={
                          <ProtectedRoute>
                            <PurchasesPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/affiliations"
                        element={
                          <ProtectedRoute>
                            <Container maxWidth="lg" sx={{ py: 4 }}>
                              <AffiliatePage />
                            </Container>
                          </ProtectedRoute>
                        }
                      />

                      {/* Admin routes avec Container */}
                      <Route
                        path="/admin/login"
                        element={
                          <Container maxWidth="lg" sx={{ py: 4 }}>
                            <AdminLogin />
                          </Container>
                        }
                      />
                      <Route
                        path="/admin/dashboard"
                        element={
                          <PrivateRoute>
                            <Container maxWidth="lg" sx={{ py: 4 }}>
                              <AdminDashboard />
                            </Container>
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/admin/details/:id"
                        element={
                          <PrivateRoute>
                            <Container maxWidth="lg" sx={{ py: 4 }}>
                              <AdminCarDetails />
                            </Container>
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/admin/create"
                        element={
                          <PrivateRoute>
                            <Container maxWidth="lg" sx={{ py: 4 }}>
                              <CreateCar />
                            </Container>
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/admin/edit/:id"
                        element={
                          <PrivateRoute>
                            <Container maxWidth="lg" sx={{ py: 4 }}>
                              <EditCar />
                            </Container>
                          </PrivateRoute>
                        }
                      />

                      <Route
                        path="/admin/orders"
                        element={
                          <PrivateRoute>
                            <AdminOrdersPage />
                          </PrivateRoute>
                        }
                      />

                      <Route
                        path="/admin/affiliations"
                        element={
                          <PrivateRoute>
                            <AdminAffiliationsPage />
                          </PrivateRoute>
                        }
                      />

                      {/* Redirect unknown routes */}
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </Box>

                  <Footer />
                </Box>
              </FilterProvider>
            </CompareProvider>
          </CartProvider>
        </AuthProvider>
      </ThemeProvider>
    </Suspense>
  );
}

export default App;
